import React from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  Switch,
  Button,
  Icon,
  Typography,
  MenuItem,
  Select,
  SelectChangeEvent,
  InputAdornment,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  IconButton,
  Menu,
  Skeleton,
  Tooltip,
  TooltipProps,
  Dialog,
  DialogTitle,
  DialogContent,
  FormHelperText,
  DialogActions,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { PermissionsSelector } from "../../components/PermissionsSelector/PermissionsSelector";
import { staffService } from "../../services/staff.service";
import { configService } from "../../services/config.service";
import { facilitiesService } from "../../services/facilities.service";
import { IESDoc } from "../../../../app.interface";
import { useAuth } from "../../../auth/hooks/useAuth";
import { LoadingState } from "./components/LoadingState";
import { MuiTelInput } from "mui-tel-input";
import styled from "@emotion/styled";
import { Theme } from "@mui/material/styles";
import { StaffBuilderPermissionsManager } from "./components/staffBuilder.permissionsManager";
import { StaffBuilderPersonalInformation } from "./components/staff-builder.personal-information";
import { StaffBuilderRoleAndAccess } from "./components/staff-builder.role-and-access";
import { StaffBuilderSystemAccess } from "./components/staff-builder.system-access";
import { StaffBuilderFacilityAssignment } from "./components/staff-builder.facility-assignment";
import { StaffBuilderLoading } from "./components/staffBuilder.loading";

interface StaffMember {
  _id?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  role: string;
  is_active: boolean;
  custom_permissions?: boolean;
  permissions?: string[];
  is_pos_user: boolean;
  is_cloud_user: boolean;
  pos_pin?: string;
  is_store_owner?: boolean;
  original_email?: string;
  is_pos_supervisor?: boolean;
  password?: string;

  //new for waiter view

  is_waiter?: boolean;
  is_cashier?: boolean;
}

interface ValidationErrors {
  [key: string]: string;
}

interface Role {
  _id: string;
  name: string;
  permissions: string[];
}

interface Permission {
  id: string;
  name: string;
}

interface PermissionGroup {
  id: string;
  name: string;
  description: string;
  icon: string;
  permissions: Permission[];
  subgroups?: PermissionGroup[];
  isSubgroup?: boolean;
}

interface BackendPermission {
  _id: string;
  name: string;
  total_cost?: number;
  price?: number;
  cost_per_unit?: number;
}

interface AssignmentOption {
  _id: string;
  name: string;
  type: "branch" | "department" | "warehouse";
  icon: string;
}

const CustomTooltip = (props: TooltipProps) => (
  <Tooltip
    {...props}
    arrow
    PopperProps={{
      sx: {
        "& .MuiTooltip-tooltip": {
          backgroundColor: "background.paper",
          color: "text.primary",
          boxShadow: 4,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          padding: "8px 12px",
          fontSize: 13,
          fontWeight: 500,
          maxWidth: "none",
          display: "flex",
          alignItems: "center",
          gap: 1,
        },
        "& .MuiTooltip-arrow": {
          color: "background.paper",
          "&:before": {
            border: "1px solid",
            borderColor: "divider",
          },
        },
      },
    }}
  />
);

const getGroupIcon = (groupName: string): string => {
  const icons: Record<string, string> = {
    dashboard: "dashboard",
    staff: "people",
    "stock-management": "inventory_2",
    raw_materials: "inventory",
    consumables: "category",
    menu: "restaurant_menu",
    recipes: "menu_book",
    sale_items: "fastfood",
    offers: "local_offer",
    suppliers: "handshake",
    facilities: "store",
    branches: "store",
    warehouses: "warehouse",
    pos: "point_of_sale",
    cashiers: "payments",
    customers: "people",
    settings: "settings",
    configuration: "admin_panel_settings",
    roles: "security",
  };
  return icons[groupName] || "extension";
};

const getGroupDescription = (groupName: string): string => {
  const descriptions: Record<string, string> = {
    dashboard: "Access to dashboard analytics and overview",
    staff: "Manage staff members and their access",
    "stock-management": "Manage inventory, raw materials, and consumables",
    raw_materials: "Manage raw materials and ingredients",
    consumables: "Manage consumable items and supplies",
    menu: "Manage menu items, recipes, and offers",
    recipes: "Create and manage kitchen recipes",
    sale_items: "Manage menu items for sale",
    offers: "Create and manage promotional offers",
    suppliers: "Manage suppliers and procurement",
    facilities: "Manage branches and warehouses",
    branches: "Manage business branches",
    warehouses: "Manage storage warehouses",
    pos: "Point of sale operations",
    cashiers: "Manage cashier operations",
    customers: "Customer management and loyalty",
    settings: "System settings and preferences",
    configuration: "System configuration and role management",
    roles: "Manage user roles and permissions",
  };
  return descriptions[groupName] || `Manage ${groupName} module`;
};

const sortPermissions = (permissions: Permission[]): Permission[] => {
  const actionOrder = { view: 1, add: 2, update: 3, delete: 4 };
  return permissions.sort((a, b) => {
    const actionA = a.name.split(".")[1];
    const actionB = b.name.split(".")[1];
    return (
      (actionOrder[actionA as keyof typeof actionOrder] || 99) -
      (actionOrder[actionB as keyof typeof actionOrder] || 99)
    );
  });
};

const organizePermissions = (
  permissions: BackendPermission[]
): PermissionGroup[] => {
  // First, group permissions by their module
  const permissionsByModule = permissions.reduce(
    (groups: { [key: string]: Permission[] }, permission) => {
      const [moduleName] = permission.name.split(".");
      const [, action] = permission.name.split(".");

      // Format the permission name
      const formattedName = `${
        action.charAt(0).toUpperCase() + action.slice(1)
      } ${moduleName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")}`;

      if (!groups[moduleName]) {
        groups[moduleName] = [];
      }

      groups[moduleName].push({
        id: permission._id,
        name: formattedName,
      });

      return groups;
    },
    {}
  );

  // Convert to array of permission groups and sort
  const groups = Object.entries(permissionsByModule).map(
    ([moduleName, permissions]) => ({
      id: moduleName,
      name: moduleName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      description: getGroupDescription(moduleName),
      icon: getGroupIcon(moduleName),
      permissions: sortPermissions(permissions),
    })
  );

  return groups;
};

const StyledTelInput = styled(MuiTelInput)<{ theme?: Theme }>`
  & .MuiOutlinedInput-root {
    border-radius: 4px;
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme?.palette.primary.main};
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme?.palette.primary.main};
      border-width: 2px;
    }
  }
  & .MuiInputLabel-root {
    color: ${({ theme }) => theme?.palette.text.secondary};
  }
`;

export const StaffBuilder: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const { user } = useAuth();
  const [permissionGroups, setPermissionGroups] = React.useState<
    PermissionGroup[]
  >([]);
  const [errors, setErrors] = React.useState<ValidationErrors>({});
  const [staffData, setStaffData] = React.useState<StaffMember>({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    role: "",
    is_active: true,
    custom_permissions: false,
    permissions: [],
    is_pos_user: false,
    is_cloud_user: true,
    pos_pin: "",
    is_pos_supervisor: false,

    //new for waiter view
    is_waiter: false,
    is_cashier: false,
  });
  const [selectedAssignment, setSelectedAssignment] = React.useState<IESDoc>();
  const [roles, setRoles] = React.useState<IESDoc[]>([]);
  const [assignmentOptions, setAssignmentOptions] = React.useState<IESDoc[]>(
    []
  );
  const [branchesMenuAnchor, setBranchesMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const [permissionViewOpen, setPermissionViewOpen] = React.useState(false);

  // Add a new loading state for data fetching
  const [dataLoading, setDataLoading] = React.useState(false);

  // Add email change warning dialog state
  const [emailChangeDialogOpen, setEmailChangeDialogOpen] =
    React.useState(false);
  const [pendingEmailChange, setPendingEmailChange] = React.useState<
    string | null
  >(null);

  const [permissions, setPermissions] = React.useState<Permission[]>([]);

  React.useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      if (id) {
        setInitialLoading(true);
      } else {
        setDataLoading(true);
      }

      const [
        rolesResponse,
        branchesResponse,
        departmentsResponse,
        warehousesResponse,
        permissionsResponse,
      ] = await Promise.all([
        configService.listRoles({}),
        configService.listBranches({}),
        facilitiesService.listDepartments({}),
        facilitiesService.listWarehouses({}),
        configService.listPermissions({
          pageSize: 10000,
          pageIndex: 0,
        }),
      ]);

      setRoles(rolesResponse.data || []);

      // Map permissions to the correct format
      const mappedPermissions = (
        permissionsResponse.data as BackendPermission[]
      ).map((permission) => ({
        id: permission._id,
        name: permission.name, // Already in correct format "resource.action"
      }));
      setPermissions(mappedPermissions);

      const branchOptions: IESDoc[] = (branchesResponse?.data || []).map(
        (branch: IESDoc) => ({
          _id: branch._id,
          name: branch.name,
          type: "branch",
          icon: "store",
        })
      );

      const warehouseOptions: IESDoc[] = (warehousesResponse?.data || []).map(
        (warehouse: IESDoc) => ({
          _id: warehouse._id,
          name: warehouse.name,
          type: "warehouse",
          icon: "warehouse",
        })
      );

      const departmentOptions: IESDoc[] = (departmentsResponse?.data || []).map(
        (dept: IESDoc) => ({
          _id: dept._id,
          name: dept.name,
          type: "department",
          icon: "business",
        })
      );

      const allOptions = [
        ...branchOptions,
        ...warehouseOptions,
        ...departmentOptions,
      ];
      setAssignmentOptions(allOptions);

      if (id) {
        const response = await staffService.getStaffMember(id);
        const staffMember = response.data.data;

        // Find the assigned role using role_id
        const assignedRole = rolesResponse.data.find(
          (role: IESDoc) => role._id === staffMember.role_id
        );

        // Find the assigned facility using assignment_details.id
        const assignedFacility = allOptions.find(
          (opt) => opt._id === staffMember.assignment_details?.id
        );

        setStaffData({
          ...staffMember,
          role: staffMember.role_id,
          first_name: staffMember.first_name,
          last_name: staffMember.last_name,
          phone_number: staffMember.phone_number || "",
          is_pos_user: staffMember.is_pos_user || false,
          is_store_owner: staffMember.is_store_owner || false,
          original_email: staffMember.email,
          custom_permissions: assignedRole
            ? !arraysEqual(assignedRole.permissions, staffMember.permissions)
            : false,
          is_pos_supervisor: staffMember.is_pos_supervisor || false,

          //new for waiter view

          is_waiter: staffMember.is_waiter || false,
          is_cashier: staffMember.is_cashier || false,
        });

        if (assignedFacility) {
          setSelectedAssignment(assignedFacility);
        }
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setInitialLoading(false);
      setDataLoading(false);
    }
  };

  // Helper function to compare arrays
  const arraysEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  const handleFieldChange = (field: keyof StaffMember) => (value: string) => {
    setStaffData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePhoneChange = (value: string) => {
    setStaffData((prev) => ({ ...prev, phone_number: value }));
    if (errors.phone_number) {
      setErrors((prev) => ({ ...prev, phone_number: "" }));
    }
  };

  const handleSelectChange =
    (field: keyof StaffMember) => (event: SelectChangeEvent) => {
      setStaffData((prev) => {
        const newData = { ...prev, [field]: event.target.value };
        if (field === "role" && !prev.custom_permissions) {
          const selectedRole = roles.find((r) => r._id === event.target.value);
          newData.permissions = selectedRole?.permissions || [];
        }
        return newData;
      });
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

  const handleStatusChange = (
    event: React.MouseEvent<HTMLElement>,
    newStatus: boolean | null
  ) => {
    if (newStatus !== null) {
      setStaffData((prev) => ({ ...prev, is_active: newStatus }));
    }
  };

  const handleCustomPermissionsToggle = () => {
    setStaffData((prev) => {
      const newData = {
        ...prev,
        custom_permissions: !prev.custom_permissions,
      };
      if (!newData.custom_permissions) {
        const selectedRole = roles.find((r) => r._id === prev.role);
        newData.permissions = selectedRole?.permissions || [];
      }
      return newData;
    });
  };

  const handlePermissionsChange = (permissions: string[]) => {
    setStaffData((prev) => ({ ...prev, permissions }));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!staffData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }

    if (!staffData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }

    if (!staffData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(staffData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!staffData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    }

    if (!staffData.is_store_owner) {
      if (!staffData.role) {
        newErrors.role = "Role is required";
      }

      if (!selectedAssignment) {
        newErrors.assignment = "Assignment is required";
      }
    }

    // Check that at least one access type is enabled
    if (!staffData.is_pos_user && !staffData.is_cloud_user) {
      newErrors.access = "At least one access type must be enabled";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = (): boolean => {
    const basicValidation =
      !!staffData.first_name.trim() &&
      !!staffData.last_name.trim() &&
      !!staffData.email.trim() &&
      !!staffData.phone_number.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(staffData.email);

    const accessValidation = staffData.is_pos_user || staffData.is_cloud_user;

    if (staffData.is_store_owner) {
      return basicValidation && accessValidation;
    }

    return (
      basicValidation &&
      !!staffData.role &&
      !!selectedAssignment &&
      accessValidation
    );
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Check for email change and show warning if needed
    if (
      id &&
      staffData.original_email &&
      staffData.email !== staffData.original_email
    ) {
      setPendingEmailChange(staffData.email);
      setEmailChangeDialogOpen(true);
      return;
    }

    await saveStaffMember();
  };

  const saveStaffMember = async () => {
    try {
      setLoading(true);
      const staffPayload = {
        first_name: staffData.first_name,
        last_name: staffData.last_name,
        email: staffData.email,
        phone_number: staffData.phone_number,
        role: staffData.role,
        is_active: staffData.is_active,
        permissions: staffData.permissions,
        is_pos_user: staffData.is_pos_user,
        is_cloud_user: staffData.is_cloud_user,
        pos_pin: staffData.pos_pin == "" ? undefined : staffData.pos_pin,
        assignment: selectedAssignment?._id,
        is_pos_supervisor: staffData.is_pos_supervisor,

        //new for waiter view
        is_waiter: staffData.is_waiter,
        is_cashier: staffData.is_cashier,

        password: staffData.password,
      };

      if (id) {
        await staffService.updateStaffMember({ ...staffPayload, id });
        toast.success("Staff member updated successfully");
      } else {
        await staffService.createStaffMember(staffPayload);
        toast.success("Staff member created successfully");
      }
      navigate("/business/configuration/staff");
    } catch (error) {
      toast.error(
        id ? "Failed to update staff member" : "Failed to create staff member"
      );
      console.error("Error saving staff:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle system access toggle
  const handleSystemAccessToggle =
    (type: "pos" | "cloud") => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;

      if (type === "pos") {
        // If turning off POS access, ensure cloud access is on
        if (!newValue && !staffData.is_cloud_user) {
          setStaffData((prev) => ({
            ...prev,
            is_pos_user: newValue,
            is_cloud_user: true,
          }));
        } else {
          setStaffData((prev) => ({
            ...prev,
            is_pos_user: newValue,
          }));
        }
      } else {
        // If turning off cloud access, ensure POS access is on
        if (!newValue && !staffData.is_pos_user) {
          setStaffData((prev) => ({
            ...prev,
            is_cloud_user: newValue,
            is_pos_user: true,
          }));
        } else {
          setStaffData((prev) => ({
            ...prev,
            is_cloud_user: newValue,
          }));
        }
      }

      if (errors.access) {
        setErrors((prev) => ({ ...prev, access: "" }));
      }
    };

  // Add this function to handle POS supervisor toggle
  const handlePOSSupervisorToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setStaffData((prev) => ({
      ...prev,
      is_pos_supervisor: event.target.checked,
    }));
  };

  //new for waiter view
  const handleWaiterToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStaffData((prev) => ({
      ...prev,
      is_waiter: event.target.checked,
    }));
  };

  const handleCashierToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStaffData((prev) => ({
      ...prev,
      is_cashier: event.target.checked,
    }));
  };

  // Add new handlers for PIN and password
  const handlePinChange = (pin: string) => {
    setStaffData((prev) => ({
      ...prev,
      pos_pin: pin,
    }));
  };

  const handlePasswordChange = (password: string) => {
    // In a real application, you would typically hash the password
    // before saving it to the state
    setStaffData((prev) => ({
      ...prev,
      password: password,
    }));
  };

  if (initialLoading) {
    return <StaffBuilderLoading />;
  }

  return (
    <Box>
      <PageHeader
        title={id ? "Edit Staff Member" : "Add New Staff Member"}
        subtitle="Manage staff information and permissions"
        icon="assignment_ind"
        backButton
        onBack={() => navigate("/business/configuration/staff")}
      />

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={6}>
          <StaffBuilderPersonalInformation
            firstName={staffData.first_name}
            lastName={staffData.last_name}
            email={staffData.email}
            phoneNumber={staffData.phone_number}
            loading={loading}
            errors={errors}
            onFirstNameChange={(value) =>
              handleFieldChange("first_name")(value)
            }
            onLastNameChange={(value) => handleFieldChange("last_name")(value)}
            onEmailChange={(value) => handleFieldChange("email")(value)}
            onPhoneNumberChange={handlePhoneChange}
          />
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={6}>
          <StaffBuilderRoleAndAccess
            staffData={staffData}
            errors={errors}
            loading={loading}
            roles={roles}
            handleSelectChange={handleSelectChange}
            handleStatusChange={handleStatusChange}
            setPermissionViewOpen={setPermissionViewOpen}
          />
        </Grid>

        {/* Full Width System Access Section */}
        <Grid item xs={12}>
          <StaffBuilderSystemAccess
            staffData={staffData}
            errors={errors}
            onSystemAccessToggle={handleSystemAccessToggle}
            onPOSSupervisorToggle={handlePOSSupervisorToggle}
            onWaiterToggle={handleWaiterToggle}
            onCashierToggle={handleCashierToggle}
            onPinChange={handlePinChange}
            onPasswordChange={handlePasswordChange}
          />
        </Grid>

        {/* Full Width Facility Assignment Section */}
        <Grid item xs={12}>
          <StaffBuilderFacilityAssignment
            selectedAssignment={selectedAssignment}
            assignmentOptions={assignmentOptions}
            errors={errors}
            dataLoading={dataLoading}
            onAssignmentSelect={setSelectedAssignment}
          />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "flex-end",
          mt: 4,
          mb: 4,
          position: "relative",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate("/business/configuration/staff")}
          startIcon={<Icon>arrow_back</Icon>}
          sx={{ px: 3, py: 1, borderRadius: 1 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
          startIcon={loading ? undefined : <Icon>save</Icon>}
          sx={{ px: 3, py: 1, minWidth: 180, borderRadius: 1 }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon>hourglass_empty</Icon>
              {id ? "Updating..." : "Creating..."}
            </Box>
          ) : id ? (
            "Update Staff Member"
          ) : (
            "Create Staff Member"
          )}
        </Button>
      </Box>

      {/* Permission Management Dialog */}
      <StaffBuilderPermissionsManager
        open={permissionViewOpen}
        onClose={() => setPermissionViewOpen(false)}
        permissions={permissions}
        selectedPermissions={staffData.permissions || []}
        onChange={handlePermissionsChange}
        customPermissions={staffData.custom_permissions || false}
        onCustomPermissionsChange={(value) => {
          setStaffData((prev) => ({
            ...prev,
            custom_permissions: value,
            permissions: value
              ? prev.permissions
              : roles.find((r) => r._id === prev.role)?.permissions || [],
          }));
        }}
        hasRole={!!staffData.role}
      />

      {/* Add Email Change Warning Dialog */}
      <Dialog
        open={emailChangeDialogOpen}
        onClose={() => {
          setEmailChangeDialogOpen(false);
          setPendingEmailChange(null);
          // Revert to original email if dialog is closed
          if (staffData.original_email) {
            setStaffData((prev) => ({
              ...prev,
              email: staffData.original_email as string,
            }));
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon color="warning">warning</Icon>
            Change Login Email
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            You are about to change the email address used for logging into the
            system.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The staff member will need to use the new email address (
            {pendingEmailChange}) for future logins. Are you sure you want to
            proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEmailChangeDialogOpen(false);
              setPendingEmailChange(null);
              // Revert to original email
              if (staffData.original_email) {
                setStaffData((prev) => ({
                  ...prev,
                  email: staffData.original_email as string,
                }));
              }
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (pendingEmailChange) {
                setStaffData((prev) => ({
                  ...prev,
                  email: pendingEmailChange,
                }));
              }
              setEmailChangeDialogOpen(false);
              setPendingEmailChange(null);
            }}
            variant="contained"
            color="primary"
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
